'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { type FavoriteTeam,  } from '@/lib/supabase-realtime';
import { Star, Bell, BellOff, Heart, HeartOff, Trophy } from 'lucide-react';
import { getFlag, TEAM_FLAGS } from '@/lib/api-football';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';



const ALL_TEAMS = Object.keys(TEAM_FLAGS).map((name, i) => ({
  id: i + 1,
  name,
  code: name.slice(0, 3).toUpperCase(),
  flag: TEAM_FLAGS[name],
}));

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Register the service worker and return the registration */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.error('[SW] Registration failed:', err);
    return null;
  }
}

export default function FavoritesPage() {
  const [favorites, setFavorites] = useState<FavoriteTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [pushSub, setPushSub] = useState<PushSubscription | null>(null);
  const [swSupported, setSwSupported] = useState(false);
  const { t } = useLanguage();

  const fetchFavorites = useCallback(async () => {
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Check push notification status and register SW
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setSwSupported(supported);
    if (!supported) return;

    registerServiceWorker().then((reg) => {
      if (!reg) return;
      reg.pushManager.getSubscription().then((sub) => {
        setPushEnabled(!!sub);
        if (sub) setPushSub(sub);
      }).catch(() => {});
    });
  }, []);

  const toggleFavorite = async (teamId: number, teamName: string) => {
    setTogglingId(teamId);
    const isFav = favorites.some(f => f.teamId === teamId);
    if (isFav) {
      setFavorites(prev => prev.filter(f => f.teamId !== teamId));
    } else {
      setFavorites(prev => [...prev, { id: '', userId: '', teamId, teamName, teamLogo: '', teamCode: teamName.slice(0, 3).toUpperCase() }]);
    }
    setTogglingId(null);
  };

  const togglePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled && pushSub) {
        // Unsubscribe
        await pushSub.unsubscribe();
        // Remove from backend
        await fetch('/api/push-subscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: pushSub.endpoint }),
        });
        setPushEnabled(false);
        setPushSub(null);
      } else {
        if (!swSupported) {
          alert(t.favorites.pushNotSupported);
          return;
        }
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          alert('Notification permission denied. Please allow notifications in your browser settings.');
          return;
        }

        // Register SW if not already
        const reg = await registerServiceWorker();
        if (!reg) {
          alert('Service worker registration failed. Please try again.');
          return;
        }

        // Subscribe to push
        const subscribeOptions: PushSubscriptionOptionsInit = {
          userVisibleOnly: true,
        };
        if (VAPID_PUBLIC_KEY) {
          subscribeOptions.applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        }

        const sub = await reg.pushManager.subscribe(subscribeOptions);

        // Save to backend via API route
        const subJson = sub.toJSON();
        await fetch('/api/push-subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: '',
            subscription: {
              endpoint: sub.endpoint,
              keys: subJson.keys,
            },
          }),
        });

        setPushEnabled(true);
        setPushSub(sub);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
      alert('Failed to toggle notifications. Please try again.');
    } finally {
      setPushLoading(false);
    }
  };

  const favIds = new Set(favorites.map(f => f.teamId));
  const filtered = ALL_TEAMS.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <AppLayout activeRoute="/favorites">
      <div className="space-y-6 pb-20 lg:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <Star size={22} className="text-orange-400" />
              {t.favorites.title}
            </h1>
            <p className="text-sm text-slate-400 mt-0.5">{t.favorites.subtitle}</p>
          </div>
        </div>

        <>
            {/* Push Notifications Card — only shown when logged in */}
            <div
              className="rounded-2xl p-4 flex items-center justify-between gap-4"
              style={{
                background: pushEnabled ? 'hsl(25 95% 53% / 0.08)' : 'hsl(222 35% 11%)',
                border: `1px solid ${pushEnabled ? 'hsl(25 95% 53% / 0.25)' : 'hsl(222 25% 16%)'}`,
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: pushEnabled ? 'hsl(25 95% 53% / 0.15)' : 'hsl(222 25% 18%)' }}>
                  {pushEnabled ? <Bell size={18} className="text-orange-400" /> : <BellOff size={18} className="text-slate-500" />}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">
                    {pushEnabled ? t.favorites.notificationsOn : t.favorites.notificationsOff}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {!swSupported ? t.favorites.pushNotSupported : pushEnabled ? t.favorites.enableNotifications : t.favorites.disableNotifications}
                  </div>
                </div>
              </div>
              <button
                onClick={togglePushNotifications}
                disabled={pushLoading || !swSupported}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
                style={{
                  background: pushEnabled ? 'hsl(0 84% 60% / 0.15)' : 'hsl(25 95% 53%)',
                  color: pushEnabled ? 'hsl(0 84% 70%)' : 'white',
                  border: pushEnabled ? '1px solid hsl(0 84% 60% / 0.3)' : 'none',
                }}
              >
                {pushLoading ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : pushEnabled ? (
                  <><BellOff size={14} /> {t.favorites.disableNotifications.split(' ')[0]}</>
                ) : (
                  <><Bell size={14} /> {t.favorites.enableNotifications.split(' ')[0]}</>
                )}
              </button>
            </div>

            {/* Favorites Summary */}
            {favorites.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Heart size={15} className="text-orange-400" />
                  <span className="text-sm font-semibold text-white">{t.favorites.following} ({favorites.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {favorites.map(fav => (
                    <div
                      key={fav.teamId}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: 'hsl(25 95% 53% / 0.1)', border: '1px solid hsl(25 95% 53% / 0.2)' }}
                    >
                      <span className="text-lg">{getFlag(fav.teamName)}</span>
                      <Link href={`/team/${fav.teamId}`} className="text-sm font-medium text-white hover:text-orange-400 transition-colors">
                        {fav.teamName}
                      </Link>
                      <button
                        onClick={() => toggleFavorite(fav.teamId, fav.teamName)}
                        className="text-slate-500 hover:text-red-400 transition-colors ml-1"
                      >
                        <HeartOff size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team Search */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={15} className="text-slate-400" />
                <span className="text-sm font-semibold text-white">{t.favorites.allTeams}</span>
              </div>
              <input
                type="text"
                placeholder={t.favorites.searchTeams}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none mb-4 transition-all"
                style={{ background: 'hsl(222 35% 13%)', border: '1px solid hsl(222 25% 18%)' }}
              />

              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} className="rounded-xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 80 }} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filtered.map(team => {
                    const isFav = favIds.has(team.id);
                    const isToggling = togglingId === team.id;
                    return (
                      <button
                        key={team.id}
                        onClick={() => toggleFavorite(team.id, team.name)}
                        disabled={isToggling}
                        className="rounded-xl p-3 flex items-center gap-3 text-left transition-all duration-200 hover:scale-[1.02] disabled:opacity-60"
                        style={{
                          background: isFav ? 'hsl(25 95% 53% / 0.1)' : 'hsl(222 35% 11%)',
                          border: `1px solid ${isFav ? 'hsl(25 95% 53% / 0.3)' : 'hsl(222 25% 16%)'}`,
                        }}
                      >
                        <span className="text-2xl">{team.flag}</span>
                        <div className="flex-1 min-w-0">
                          <Link
                            href={`/team/${team.id}`}
                            onClick={e => e.stopPropagation()}
                            className="text-sm font-medium text-white truncate block hover:text-orange-400 transition-colors"
                          >
                            {team.name}
                          </Link>
                          <div className="text-xs text-slate-600">{team.code}</div>
                        </div>
                        <div className="flex-shrink-0">
                          {isToggling ? (
                            <span className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin block" />
                          ) : isFav ? (
                            <Star size={15} className="text-orange-400 fill-orange-400" />
                          ) : (
                            <Star size={15} className="text-slate-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
      </div>
    </AppLayout>
  );
}
